import { Big } from 'big.js';
import type { Order } from '@/api/spec';
import { Side } from '@/common/constants';
import { isGteTriggerCondition, isLteTriggerCondition } from '@/feature/order_entry/trigger_condition';
import { stringToBig } from '@/utils/bigUtils';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';

export interface LinkedTpSl {
  takeProfit?: number;
  stopLoss?: number;
  takeProfitLimitPrice?: number;
  stopLossLimitPrice?: number;
  tpSlPercentage?: number;
}

/**
 * Reads the TP/SL view fields off a position's linked orders. TP vs SL is matched by GTE/LTE
 * direction (last or mark): long → TP=GTE, SL=LTE; short → TP=LTE, SL=GTE. Prices are descaled by
 * quote decimals; the size percentage is the closed fraction of the position (capped at 100%).
 */
export function extractLinkedTpSl(
  orders: Order[],
  side: Side,
  quoteDecimals: number,
  assetDecimals: number,
  positionSize: number
): LinkedTpSl {
  const tpOrder = orders.find((o) => (side === Side.long ? isGteTriggerCondition(o.tpc) : isLteTriggerCondition(o.tpc)));
  const slOrder = orders.find((o) => (side === Side.long ? isLteTriggerCondition(o.tpc) : isGteTriggerCondition(o.tpc)));

  const extract = (order: Order | undefined) => {
    if (!order?.tp) return undefined;

    const triggerPrice = stringToBig(order.tp).div(Big(10).pow(quoteDecimals)).toNumber();
    let limitPrice: number | undefined;

    if (order.p) {
      const limitBig = stringToBig(order.p).div(Big(10).pow(quoteDecimals));
      if (limitBig.gt(0)) {
        limitPrice = limitBig.toNumber();
      }
    }

    const orderSize = offsetNumberToNumber(order.os, assetDecimals);
    const percentage = positionSize > 0 ? Math.min(Math.round((orderSize / positionSize) * 100 * 100) / 100, 100) : undefined;

    return { triggerPrice, limitPrice, percentage };
  };

  const result: LinkedTpSl = {};

  const tp = extract(tpOrder);
  if (tp) {
    result.takeProfit = tp.triggerPrice;
    result.takeProfitLimitPrice = tp.limitPrice;
    result.tpSlPercentage = tp.percentage;
  }

  const sl = extract(slOrder);
  if (sl) {
    result.stopLoss = sl.triggerPrice;
    result.stopLossLimitPrice = sl.limitPrice;
    if (result.tpSlPercentage == null) {
      result.tpSlPercentage = sl.percentage;
    }
  }

  return result;
}
